import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, Input, Select } from '@repo/ui';
import { GroupByOption } from '@repo/types';
import { QueryStringParams } from '@xtnd/web/shared';
import {
  ArCollectionQueueFilter,
  ArCollectionQueueParams,
  DaysPastDueOptions,
  GROUP_BY,
  GroupByOptions,
  GroupBySelectOption,
} from '../../types';
import CollectionExportButton from './CollectionExportButton';

type ActionToolbarProps = {
  isDataTableLoading: boolean;
  filters: ArCollectionQueueFilter;
  queryParams: QueryStringParams;
  onChange: (next: ArCollectionQueueFilter) => void;
};

export default function ActionToolbar({
  isDataTableLoading,
  filters,
  queryParams,
  onChange,
}: ActionToolbarProps) {
  const { search = '', groupBy, daysPastDue } = filters;
  const [keyForSearch, setKeyForSearch] = useState(search);

  const exportFilename = useMemo(
    () => `collection-groupBy-${filters.groupBy}`,
    [filters.groupBy]
  );

  const exportMeta = useMemo(() => {
    const groupByLabel = GroupByOptions.find(o => o.value === groupBy)?.label || '';
    const daysPastDueLabel = DaysPastDueOptions.find(o => o.value === `${daysPastDue}`)?.label || '';
    return {
      title: `Outstanding AR Invoices`,
      headers: [
        ['Group By', groupByLabel],
        ['Days Past Due', daysPastDueLabel],
      ],
    };
  }, [groupBy, daysPastDue]);

  const update = useCallback(
    (patch: Partial<ArCollectionQueueFilter>) => {
      onChange({ ...filters, ...patch });
    },
    [filters, onChange]
  );

  // Sync keyForSearch with search from URL/filters (when navigating from QuickSearch)
  useEffect(() => {
    setKeyForSearch(search);
  }, [search]);

  useEffect(() => {
    if (search === keyForSearch) return;

    const timeoutDebounced = setTimeout(() => {
      update({ search: keyForSearch });
    }, 1000);

    return () => clearTimeout(timeoutDebounced);
  }, [keyForSearch, search, update]);

  const searchTextPlaceholder = useMemo(() => {
    if (filters.groupBy === GroupByOption.NONE) return 'Search matter, client, or attorney';
    if (filters.groupBy === GroupByOption.CLIENT) return 'Search client';

    return 'Search attorney';
  }, [filters]);

  return (
    <div className="flex justify-between w-full text-sm">
      <div className="relative flex-1 duration-300 max-w-md">
        <Icon
          name="HiSearch"
          size="sm"
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
        />
        <Input
          value={keyForSearch}
          variant="outlined"
          onChange={e => setKeyForSearch(e.target.value)}
          placeholder={searchTextPlaceholder}
          className="flex h-10 w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
        />
      </div>

      <div className="flex gap-4">
        <Select<GroupBySelectOption>
          className="w-fit min-w-60"
          options={GroupByOptions}
          value={groupBy}
          disabled={isDataTableLoading}
          onChange={option => {
            if (!option) return;
            if (typeof option === 'string') {
              update({ groupBy: option as GROUP_BY });
              return;
            }

            update({ groupBy: option.value });
          }}
        />
        <Select
          className="w-fit min-w-30"
          options={DaysPastDueOptions}
          disabled={isDataTableLoading}
          value={`${daysPastDue}`}
          onChange={option => {
            if (typeof option === 'string') {
              const value = parseInt(option, 10);
              update({ daysPastDue: isNaN(value) ? 30 : value });
            }
          }}
        />
        <CollectionExportButton
          queryParams={queryParams as ArCollectionQueueParams}
          exportFilename={exportFilename}
          isDataLoading={isDataTableLoading}
          exportMeta={exportMeta}
        />
      </div>
    </div>
  );
}
